import { useId, type CSSProperties } from "react";
import { FigmaSvgLayer } from "./FigmaSvgLayer";
import { energyColorForBatteryLevel, fuelColorForFuelLevel } from "../../config/vehicleEnergy";
import "./NightStatusBar.css";

const BATTERY_BODY_PATH =
  "M184 52C184 47.7866 184 45.1799 185.011 43.6666C185.449 43.0114 186.011 42.4489 186.667 42.0112C188.18 41 190.287 41 194.5 41H207C211.213 41 213.32 41 214.833 42.0112C215.489 42.4489 216.051 43.0114 216.489 43.6666C217.5 45.1799 217.5 47.7866 217.5 52C217.5 56.2134 217.5 58.8201 216.489 60.3334C216.051 60.9886 215.489 61.5511 214.833 61.9888C213.32 63 211.213 63 207 63H194.5C190.287 63 188.18 63 186.667 61.9888C186.011 61.5511 185.449 60.9886 185.011 60.3334C184 58.8201 184 56.2134 184 52Z";
const BATTERY_CAP_PATH =
  "M218 49C218 48.7239 218.224 48.5 218.5 48.5C219.328 48.5 220 49.1716 220 50V54C220 54.8284 219.328 55.5 218.5 55.5C218.224 55.5 218 55.2761 218 55V49Z";
const BATTERY_BODY_WIDTH = 33.5;
const FUEL_DROP_PATH =
  "M14 1.5C14 1.5 5 13.1 5 21.1C5 27.2 8.9 31 14 31C19.1 31 23 27.2 23 21.1C23 13.1 14 1.5 14 1.5Z";
const FUEL_DROP_HEIGHT = 32;

function safeInteger(value: number) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? Math.round(numericValue) : 0;
}

function safePercent(value: number) {
  return Math.max(0, Math.min(100, safeInteger(value)));
}

export type NightStatusBarProps = {
  batteryLevel: number;
  rangeKm: number;
  fuelLevel: number;
  fuelRangeKm: number;
  time: string;
  avatarSrc: string;
  evaSrc?: string;
  topDockSrc: string;
  voiceCaption?: {
    active: boolean;
    phase: string;
    text: string;
  };
};

export function NightStatusBar({
  batteryLevel,
  rangeKm,
  fuelLevel,
  fuelRangeKm,
  time,
  avatarSrc,
  topDockSrc
}: NightStatusBarProps) {
  const clipIdBase = useId().replace(/:/g, "");
  const safeBattery = safePercent(batteryLevel);
  const safeRange = Math.max(0, safeInteger(rangeKm));
  const safeFuel = safePercent(fuelLevel);
  const safeFuelRange = Math.max(0, safeInteger(fuelRangeKm));
  const batteryFillWidth = (BATTERY_BODY_WIDTH * safeBattery) / 100;
  const energyColor = energyColorForBatteryLevel(safeBattery);
  const fuelColor = fuelColorForFuelLevel(safeFuel);
  const fuelFillHeight = (FUEL_DROP_HEIGHT * safeFuel) / 100;
  const fuelFillY = FUEL_DROP_HEIGHT - fuelFillHeight;
  const batteryFillClipId = `night-topbar-battery-fill-clip-${clipIdBase}`;
  const fuelFillClipId = `night-topbar-fuel-fill-clip-${clipIdBase}`;

  return (
    <header className="night-topbar" aria-label="夜间车机状态栏">
      <div className="night-topbar__avatar" aria-label="用户头像">
        <img className="night-topbar__avatar-source" src={avatarSrc} alt="" draggable={false} />
      </div>

      <div
        className="night-topbar__energy-island"
        aria-label={`电量 ${safeBattery}%，电动续航 ${safeRange} 公里，油量 ${safeFuel}%，燃油续航 ${safeFuelRange} 公里`}
        style={{ "--energy-color": energyColor, "--fuel-color": fuelColor } as CSSProperties}
      >
        <span className="night-topbar__energy-group">
          <svg
            className="night-topbar__battery-icon"
            viewBox="184 41 36 22"
            aria-hidden="true"
          >
            <defs>
              <clipPath id={batteryFillClipId}>
                <rect x="184" y="41" width={batteryFillWidth} height="22" />
              </clipPath>
            </defs>
            <path d={BATTERY_BODY_PATH} fill="white" fillOpacity="0.16" />
            <path d={BATTERY_BODY_PATH} fill={energyColor} clipPath={`url(#${batteryFillClipId})`} />
            <path d={BATTERY_CAP_PATH} fill="white" fillOpacity="0.16" />
          </svg>
          <span className="night-topbar__energy-text">
            <span className="night-topbar__energy-text-value">
              <span className="night-topbar__energy-level">{safeBattery}%</span>
              <span className="night-topbar__energy-range">{safeRange}km</span>
            </span>
          </span>
        </span>
        <span className="night-topbar__energy-divider" aria-hidden="true" />
        <span className="night-topbar__energy-group night-topbar__energy-group--fuel">
          <svg
            className="night-topbar__fuel-icon"
            viewBox="0 0 28 32"
            aria-hidden="true"
          >
            <defs>
              <clipPath id={fuelFillClipId}>
                <rect x="0" y={fuelFillY} width="28" height={fuelFillHeight} />
              </clipPath>
            </defs>
            <path d={FUEL_DROP_PATH} fill="white" fillOpacity="0.16" />
            <path d={FUEL_DROP_PATH} fill={fuelColor} clipPath={`url(#${fuelFillClipId})`} />
            <path d={FUEL_DROP_PATH} fill="none" stroke={fuelColor} strokeOpacity="0.68" strokeWidth="1.35" />
            <path d="M10.2 15.5C11.1 12.7 12.7 10 14 8.5" stroke="white" strokeOpacity="0.46" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <span className="night-topbar__energy-text">
            <span className="night-topbar__energy-text-value">
              <span className="night-topbar__fuel-level">{safeFuel}%</span>
              <span className="night-topbar__fuel-range">{safeFuelRange}km</span>
            </span>
          </span>
        </span>
      </div>

      <FigmaSvgLayer className="night-topbar__status-glyphs" src={topDockSrc} />
      <time className="night-topbar__time" dateTime={time}>
        {time}
      </time>
    </header>
  );
}

export function NightVoiceCaptionOverlay({
  evaSrc,
  voiceCaption
}: {
  evaSrc?: string;
  voiceCaption?: NightStatusBarProps["voiceCaption"];
}) {
  const voiceCaptionText = formatVoiceCaptionText(voiceCaption?.text ?? "");
  const shouldShowVoiceCaption = Boolean(voiceCaption?.active);
  const hasVoiceCaptionText = voiceCaptionText.length > 0;

  if (!shouldShowVoiceCaption) {
    return null;
  }

  return (
    <div
      className={[
        "night-topbar__voice-caption",
        "night-topbar__voice-caption--global",
        hasVoiceCaptionText ? "night-topbar__voice-caption--has-text" : ""
      ].filter(Boolean).join(" ")}
      aria-live="polite"
      aria-label={hasVoiceCaptionText ? `语音识别 ${voiceCaptionText}` : "语音识别中"}
    >
      <span className="night-topbar__voice-eva" aria-hidden="true">
        {evaSrc ? (
          <img className="night-topbar__voice-eva-source" src={evaSrc} alt="" draggable={false} />
        ) : (
          <span className="night-topbar__voice-eva-fallback" />
        )}
      </span>
      {hasVoiceCaptionText ? (
        <span className="night-topbar__voice-frame">
          <span className="night-topbar__voice-text">{voiceCaptionText}</span>
        </span>
      ) : null}
    </div>
  );
}

function formatVoiceCaptionText(text: string) {
  const normalizedText = text.trim().replace(/\s+/g, " ");

  if (!normalizedText) {
    return "";
  }

  const maxLength = 24;

  if (normalizedText.length <= maxLength) {
    return normalizedText;
  }

  return `...${normalizedText.slice(-maxLength)}`;
}
